/**
 * 인앱 로그인 직후 언어 통지 — 계정 프로필 언어를 앱에 알린다(규약 3.3 setLanguage 「로그인 완료 직후」).
 *
 * 규약 전문: docs/APP-BRIDGE.md §3.3 · 서버 docs/APP-PROXY-API.md
 *
 * 로그인 시점에 계정 언어를 앱에 알리는 **유일한 경로**다. setCustomer(3.8)는 언어를 싣지 않는다 —
 * 두 명령의 호출 타이밍에 따라 앱 상태가 어긋날 수 있어 언어 통지는 setLanguage 로 일원화했다
 * (2026-09-29 앱 유닛 요청).
 *
 * ── 로그인을 어떻게 아는가 ──
 * Shopify 스토어프론트에는 로그인 이벤트가 없고, 로그인은 브릿지가 없는 shopify.com 계정 지면에서
 * 일어난다. 그래서 setCustomer(3.8)와 같은 방식으로 **로그인 뒤 첫 스토어프론트 페이지 로드**에서
 * 전이를 잡는다 — 「이 고객으로 이미 통지했다」는 표시를 남기고, 표시와 지금 고객이 다르면 새 로그인이다.
 *
 * | 페이지 로드 상태 | 동작 |
 * | --- | --- |
 * | 비로그인 | 표시를 지운다 — 다음 로그인을 다시 잡기 위해서다 |
 * | 로그인 · 표시 == 지금 고객 | 아무것도 하지 않는다. 대부분의 로드가 여기서 끝난다(왕복 없음) |
 * | 로그인 · 표시 != 지금 고객 · 프로필 언어 == 지금 언어 | 통지하지 않고 표시만 기록한다 |
 * | 로그인 · 표시 != 지금 고객 · 프로필 언어 != 지금 언어 | setLanguage(프로필 언어) → 표시 기록 |
 * | 로그인 · 표시 != 지금 고객 · 프로필 언어 없음(최초 로그인) | 지금 보던 언어를 프로필에 저장 → 표시 기록 |
 *
 * ── 정책 (FP-1468, 2026-09 앱 유닛 · 제품 합의) ──
 * 언어의 기준은 **계정 프로필**이다. 기기 언어와 달라 앱이 재실행되는 불편이 있어도 프로필을 따른다 —
 * 발송물(이메일 · 푸시)과 웹이 같은 언어를 쓰려면 기기가 아니라 프로필에 있어야 하기 때문이다.
 * 기기마다 언어를 다르게 쓰는 경우는 드물다고 보고 재실행(체크아웃 흐름 끊김 포함)을 감수했다.
 * 프로필에 언어가 없는 **최초 로그인**이면 반대로 앱에서 보던 언어가 계정 언어가 된다.
 *
 * ── setLanguage 를 부르는 경우는 두 가지뿐이다 ──
 * 1. 사용자가 **명시적으로 언어를 바꿨을 때** — 마이페이지 Country & Language(piyonna-mypage-country-language.js)
 * 2. **로그인했는데 계정에 기록된 언어가 지금 언어와 다를 때** — 이 파일
 * 같은 값이면 앱이 저장만 하므로(규약 3.3) 불러도 틀리지는 않지만, 호출 조건을 이 둘로 좁혀 둔다.
 *
 * ── 왜 localStorage 인가 ──
 * app-customer.js 는 sessionStorage 를 쓰지만 여기는 반대다. sessionStorage 는 앱 재실행 · 탭마다
 * 비워지므로 로그인과 무관하게 호출이 반복된다 — setLanguage 는 값이 바뀌면 앱을 재실행시키므로
 * 「로그인할 때 한 번」이어야 한다. 탭 웹뷰끼리 localStorage 를 공유하므로(규약 1.8) 한 탭에서
 * 통지하면 다른 탭은 건너뛴다.
 *
 * ── 알아둘 것 ──
 * · 보내는 값은 **서버 프로필 언어**다. `piyonna_locale` 쿠키는 페이지 로드마다 지면 로케일로 덮이므로
 *   프로필 언어가 아니다(assets/piyonna-locale-store.js 주석).
 * · 「지금 보던 언어」는 `piyonna_locale` 이다. head 인라인이 이번 로드의 지면 로케일로 막 써 둔 값이고,
 *   인앱 지면 로케일은 앱 언어(탭 page_url 의 언어 프리픽스)를 따른다. 앱은 이미 그 언어라 setLanguage
 *   는 부르지 않는다. 국가는 기기 쿠키 값을 함께 싣는다 — 저장이 전체 교체라 빠뜨리면 지워진다
 *   (docs/APP-PROXY-API.md §3 「로그인 시점에 한 번에 송출」).
 * · 조회 · 저장 실패(네트워크 · PERMISSION_DENIED · theme dev 의 404)는 표시를 남기지 않는다 — 다음
 *   로드에서 다시 시도한다. 오류 적재는 piyonna-api.js 가 한다.
 * · 표시는 customer id 의 해시다. 식별자로 쓰지 않고 되돌릴 수도 없어 동의 철회 정리 대상이 아니다
 *   (app-customer.js 와 같은 판단, docs/CONSENT.md).
 * · 배포 직후 이미 로그인해 있던 고객은 표시가 없어 한 번 통지된다. 프로필 언어가 앱 언어와 다르면
 *   앱이 한 번 재실행된다 — 어긋나 있던 값을 맞추는 것이므로 의도된 동작이다.
 */

import { fetchCustomerProfile, saveCustomerCountryLanguage } from '@theme/piyonna-api-mypage';
import { readCountryCode, readLanguageCode } from '@theme/piyonna-locale-store';

const STORAGE_KEY = 'piyonna_app_language_synced';

/**
 * customer id 해시. 원문을 스토리지에 남기지 않기 위한 값이다 — 암호학적 용도가 아니다.
 * @param {string} value
 * @returns {string}
 */
function fingerprint(value) {
  let hash = 5381;

  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0;
  }

  return (hash >>> 0).toString(36);
}

function readSynced() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // 스토리지 차단 → 표시를 읽을 수 없다. 아래에서 쓰기도 실패하므로 로드마다 통지로 되돌아간다
    return null;
  }
}

function writeSynced(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // 기록하지 못하면 다음 로드에서 한 번 더 보낸다. 같은 값이면 앱이 저장만 한다(규약 3.3)
  }
}

function clearSynced() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // 지우지 못하면 같은 고객의 재로그인을 놓친다. 다른 고객이면 해시가 달라 잡힌다
  }
}

async function syncLanguage() {
  const app = window.PiyonnaApp;
  // 일반 브라우저에서는 스토리지도 건드리지 않는다
  if (!app || !app.isAvailable()) return;

  const config = window.PiyonnaApiConfig;
  const customerId = config && config.logged_in && config.customer_id != null ? String(config.customer_id) : '';

  if (!customerId) {
    clearSynced();
    return;
  }

  const signature = fingerprint(customerId);
  if (readSynced() === signature) return;

  let profile;
  try {
    profile = await fetchCustomerProfile();
  } catch {
    return; // 표시를 남기지 않는다 — 다음 로드에서 다시 시도
  }

  const language = profile && profile.language;

  // 최초 로그인 — 앱에서 보던 언어가 계정 언어가 된다. 앱은 이미 그 언어라 통지할 것이 없다
  if (!language) {
    const currentLanguage = readLanguageCode();
    // 지금 언어를 알 수 없으면 저장할 값이 없다. 로드마다 왕복하지 않도록 표시만 남긴다
    if (!currentLanguage) {
      writeSynced(signature);
      return;
    }

    try {
      await saveCustomerCountryLanguage({
        // 전체 교체 저장이다. 프로필에 국가만 있으면 그것을, 없으면 기기 값을 보존한다
        country_code: (profile && profile.country_code) || readCountryCode() || null,
        language: currentLanguage,
      });
    } catch {
      return; // 표시를 남기지 않는다 — 다음 로드에서 다시 시도
    }

    writeSynced(signature);
    return;
  }

  // 계정 언어가 지금 언어와 같으면 바꿀 것이 없다. 지금 언어를 알 수 없으면 통지한다 — 같은 값이면
  // 앱이 저장만 하므로(규약 3.3) 모르는 쪽으로 기울어도 해가 없다
  const currentLanguage = readLanguageCode();
  if (currentLanguage && currentLanguage === language.toLowerCase()) {
    writeSynced(signature);
    return;
  }

  // 보낸 것만 기록한다. 호출과 기록이 모두 동기라 앱 재실행보다 기록이 먼저 끝난다
  if (app.setLanguage(language)) writeSynced(signature);
}

syncLanguage();
